import { alpha, useTheme } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { ReactComponent as PlusIcon } from '../../../design-system/Icons/PlusIcon.svg';
import { ReactComponent as LayersIcon } from '../../../design-system/Icons/Layers.svg';
import { ReactComponent as RightIcon } from '../../../design-system/Icons/RightIcon.svg';
import DSBox from '../../../design-system/DSBox';
import { DSTypography } from '../../../design-system/Typography';
import DSChip from '../../../design-system/DSChip';
import { KLYDO_YELLOW } from '../../../consts/colors';
import { ArtistProfileTemplate } from '../../../api/public';
import { Section } from '../types';
import { getSectionTitle } from '../consts';

type CustomSectionsCardProps = {
  savedSections: Section[];
  displaySections: Section[];
  templates: ArtistProfileTemplate[];
  onClick: () => void;
  handleDragStart: (
    event: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => void;
  handleDragEnd: () => void;
  handleDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  handleDragEnter: (index: number) => void;
  draggedItem: (item: Section) => boolean;
};

const CustomSectionsCard = ({
  savedSections,
  displaySections,
  templates,
  onClick,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragEnter,
  draggedItem,
}: CustomSectionsCardProps) => {
  const { spacing, palette } = useTheme();

  return (
    <DSBox sx={{ pb: spacing(3) }}>
      <DSBox
        onClick={onClick}
        sx={{
          backgroundColor: 'secondary.dark',
          borderRadius: '16px',
          padding: { xxs: '30px 8px', sm: '33px' },
          cursor: 'pointer',
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'paper.elevation3',
            '& .custom-sections-icon path': { stroke: 'white' },
            '& .custom-sections-chevron': { color: 'common.white' },
          },
        }}
      >
        <DSBox
          sx={{
            display: 'flex',
            alignItems: { xxs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <DSBox
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '20px',
            }}
          >
            <DSBox
              className="custom-sections-icon"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '48px',
                height: '48px',
                borderRadius: '14px',
                backgroundColor: 'paper.elevation5',
                flexShrink: 0,
                '& path': {
                  transition: 'stroke 0.2s',
                },
              }}
            >
              <LayersIcon width={24} height={24} />
            </DSBox>
            <DSBox
              sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
            >
              <DSBox
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '28px',
                  flexWrap: 'wrap',
                }}
              >
                <DSTypography variant="h5" color="common.white">
                  Custom Sections
                </DSTypography>
                <DSChip
                  label="Optional"
                  size="small"
                  sx={{
                    backgroundColor: 'secondary.main',
                    color: 'common.white',
                    fontFamily: 'Gilroy-SemiBold',
                    fontSize: '13px',
                    letterSpacing: '0.16px',
                    lineHeight: '18px',
                    height: '24px',
                    borderRadius: '100px',
                    '& .MuiChip-label': { px: '6px' },
                    '&:hover': { backgroundColor: 'secondary.main' },
                  }}
                />
              </DSBox>
              <DSTypography variant="body2" color="text.secondary">
                Add videos, images, quotes, and stories to enrich your artist
                page
              </DSTypography>
              {savedSections.length > 0 ? (
                <DSBox sx={{ mt: spacing(1.5) }}>
                  <DSTypography
                    sx={{
                      fontFamily: 'Gilroy-Bold',
                      fontSize: '14px',
                      lineHeight: 1.43,
                      letterSpacing: '0.17px',
                      color: KLYDO_YELLOW,
                      display: 'inline',
                    }}
                  >
                    {savedSections.length}{' '}
                    {savedSections.length === 1 ? 'section' : 'sections'}
                  </DSTypography>
                  <DSTypography
                    variant="body2"
                    color="text.secondary"
                    sx={{ display: 'inline' }}
                  >
                    {' '}
                    · Drag to reorder
                  </DSTypography>
                  <DSBox
                    onClick={(event) => event.stopPropagation()}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: spacing(0.75),
                      mt: spacing(1.5),
                    }}
                  >
                    {displaySections.map((section, displayIndex) => {
                      const originalIndex = savedSections.findIndex(
                        (original) => original.id === section.id,
                      );
                      const isDragging = draggedItem(section);
                      return (
                        <DSBox
                          key={section.id}
                          draggable
                          onDragStart={(event) =>
                            handleDragStart(event, originalIndex)
                          }
                          onDragEnd={handleDragEnd}
                          onDragOver={handleDragOver}
                          onDragEnter={() => handleDragEnter(displayIndex)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: spacing(1),
                            px: spacing(1.5),
                            py: spacing(0.75),
                            backgroundColor: alpha(palette.common.white, 0.06),
                            borderRadius: spacing(1),
                            cursor: isDragging ? 'grabbing' : 'grab',
                            opacity: isDragging ? 0.4 : 1,
                            transition: 'opacity 0.2s',
                            userSelect: 'none',
                          }}
                        >
                          <DragIndicatorIcon
                            sx={{
                              color: alpha(palette.common.white, 0.3),
                              fontSize: 16,
                            }}
                          />
                          <DSTypography variant="body2">
                            {getSectionTitle(section.type, templates)}
                          </DSTypography>
                        </DSBox>
                      );
                    })}
                  </DSBox>
                </DSBox>
              ) : (
                <DSBox
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <PlusIcon
                    width={18}
                    height={18}
                    style={{
                      color: KLYDO_YELLOW,
                      flexShrink: 0,
                      display: 'block',
                      fontWeight: 'bold',
                    }}
                  />
                  <DSTypography
                    sx={{
                      fontFamily: 'Gilroy-Bold',
                      fontSize: '13px',
                      lineHeight: '22px',
                      letterSpacing: '0.46px',
                      color: KLYDO_YELLOW,
                      textTransform: 'capitalize',
                    }}
                  >
                    Add sections to get started
                  </DSTypography>
                </DSBox>
              )}
            </DSBox>
          </DSBox>
          <DSBox
            className="custom-sections-chevron"
            sx={{
              flexShrink: 0,
              alignSelf: 'center',
              transition: 'all 0.2s',
              color: alpha(palette.common.white, 0.56),
            }}
          >
            <RightIcon width={28} height={28} />
          </DSBox>
        </DSBox>
      </DSBox>
    </DSBox>
  );
};

export default CustomSectionsCard;
